/**
 * runApplyPlan — pure(-ish) executor for the Bulk Similarity Apply flow.
 *
 * Consumes a plan (built by buildApplyPlan) and per-op-kind opInputs,
 * calls the backend endpoints in the exact 4-step order documented in
 * MergePreviewModal, and returns a structured ApplyReport with per-op
 * results, timing, and error hints.
 *
 * Why a helper (and not inline in the component)?
 *  1. Testability — mock axios in a unit test and assert result shape.
 *  2. Retry — the same helper is reused with a subset of failed ops.
 *  3. Separation — component owns state + navigation; helper owns wire calls.
 *
 * Never throws — all errors are captured into `OpResult.errorDetail`.
 * Callers use `report.totals.failed > 0` to decide next steps.
 */

import { hintForError } from './errorHints';

const nowIso = () => new Date().toISOString();

const errorDetailOf = (err) => {
  // FastAPI errors put the useful message in `detail`. Axios wraps it under
  // `response.data`. Fall back to the axios message string.
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (data && typeof data === 'object') {
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail)) {
      // Pydantic validation errors — condense to one line
      return data.detail
        .map((d) => (d?.msg || JSON.stringify(d)))
        .join('; ');
    }
  }
  return err?.message || 'Unknown error';
};

const statusOf = (err) => err?.response?.status || 0;

/**
 * @param {Object}   params
 * @param {Object}   params.plan       — output of buildApplyPlan (used for row labels)
 * @param {string[]} params.skipIds
 * @param {string[]} params.keepIds
 * @param {Object}   params.analysis   — from analyzeMergeOps
 * @param {string}   params.batchId
 * @param {Object}   params.deps       — { axios, headers, API }
 * @returns {Promise<ApplyReport>}
 */
export async function runApplyPlan({
  plan,
  skipIds,
  keepIds,
  analysis,
  batchId,
  deps: { axios, headers, API },
}) {
  const startedAt = nowIso();
  const startMs = Date.now();
  const results = [];

  // Label lookup mirrors buildApplyPlan (already resolved in plan.steps),
  // but we also need to look up skip/keep row labels by id. Reuse the
  // plan.steps arrays which already carry labels.
  const stepByKind = {};
  for (const s of plan.steps) stepByKind[s.kind] = s;

  const findMergeStagingLabel = (source, target) => {
    const op = (stepByKind['merge-staging']?.ops || []).find(
      (o) => o.source.id === source && o.target.id === target,
    );
    return op ? { source: op.source, target: op.target } : {
      source: { id: source, rowNumber: null, text: source },
      target: { id: target, rowNumber: null, text: target },
    };
  };
  const findMergeApprovedLabel = (source, target) => {
    const op = (stepByKind['merge-approved']?.ops || []).find(
      (o) => o.source.id === source && o.target.id === target,
    );
    return op ? { source: op.source, target: op.target } : {
      source: { id: source, rowNumber: null, text: source },
      target: { id: target, rowNumber: null, text: target, isApproved: true },
    };
  };
  const findSkipKeepLabel = (kind, id) => {
    const op = (stepByKind[kind]?.ops || []).find((o) => o.row.id === id);
    return op ? op.row : { id, rowNumber: null, text: id };
  };

  // ---- Step 1: staging → staging merges (topologically ordered) ----
  for (const op of analysis.orderedOps || []) {
    const labels = findMergeStagingLabel(op.source_staging_id, op.target_staging_id);
    try {
      await axios.post(
        `${API}/admin/bulk-import/staging/merge-into-staging`,
        op,
        { headers },
      );
      results.push({ step: 'merge-staging', ...labels, ok: true });
    } catch (err) {
      const status = statusOf(err);
      const { hint, retryable } = hintForError(status, 'merge-staging');
      results.push({
        step: 'merge-staging',
        ...labels,
        ok: false,
        httpStatus: status,
        errorDetail: errorDetailOf(err),
        hint,
        retryable,
      });
    }
  }

  // ---- Step 2: staging → approved merges ----
  for (const { source, target } of analysis.approvedOps || []) {
    const labels = findMergeApprovedLabel(source, target);
    try {
      await axios.post(
        `${API}/admin/questions/merge`,
        {
          source_question_id: source,
          target_question_id: target,
          source_type: 'staging',
          update_answer: false,
          merge_pictures: false,
        },
        { headers },
      );
      results.push({ step: 'merge-approved', ...labels, ok: true });
    } catch (err) {
      const status = statusOf(err);
      const { hint, retryable } = hintForError(status, 'merge-approved');
      results.push({
        step: 'merge-approved',
        ...labels,
        ok: false,
        httpStatus: status,
        errorDetail: errorDetailOf(err),
        hint,
        retryable,
      });
    }
  }

  // ---- Step 3: skips (bulk endpoint) ----
  if ((skipIds || []).length > 0) {
    try {
      await axios.post(
        `${API}/admin/bulk-import/staging/bulk-delete`,
        { staging_ids: skipIds },
        { headers },
      );
      // Backend doesn't return per-id status for bulk-delete; treat the
      // whole call as pass/fail. If a subset failed silently we'll catch
      // it on the next fetch, but this endpoint is transactional in
      // practice.
      for (const id of skipIds) {
        results.push({ step: 'skip', row: findSkipKeepLabel('skip', id), ok: true });
      }
    } catch (err) {
      const status = statusOf(err);
      const { hint, retryable } = hintForError(status, 'skip');
      const detail = errorDetailOf(err);
      for (const id of skipIds) {
        results.push({
          step: 'skip',
          row: findSkipKeepLabel('skip', id),
          ok: false,
          httpStatus: status,
          errorDetail: detail,
          hint,
          retryable,
        });
      }
    }
  }

  // ---- Step 4: keeps (bulk endpoint with per-id errors[]) ----
  if ((keepIds || []).length > 0) {
    try {
      const res = await axios.post(
        `${API}/admin/bulk-import/approve`,
        { staging_ids: keepIds },
        { headers },
      );
      // Backend returns { approved_count, errors: [{staging_id, error}] }
      const errsById = new Map();
      for (const e of res.data?.errors || []) {
        errsById.set(e.staging_id || e.id, e.error || e.msg || 'Unknown error');
      }
      for (const id of keepIds) {
        if (errsById.has(id)) {
          const { hint, retryable } = hintForError(0, 'keep');
          results.push({
            step: 'keep',
            row: findSkipKeepLabel('keep', id),
            ok: false,
            httpStatus: 0,
            errorDetail: errsById.get(id),
            hint,
            retryable,
          });
        } else {
          results.push({ step: 'keep', row: findSkipKeepLabel('keep', id), ok: true });
        }
      }
    } catch (err) {
      const status = statusOf(err);
      const { hint, retryable } = hintForError(status, 'keep');
      const detail = errorDetailOf(err);
      for (const id of keepIds) {
        results.push({
          step: 'keep',
          row: findSkipKeepLabel('keep', id),
          ok: false,
          httpStatus: status,
          errorDetail: detail,
          hint,
          retryable,
        });
      }
    }
  }

  const endedAt = nowIso();
  const okCount = results.filter((r) => r.ok).length;
  const failedCount = results.length - okCount;

  return {
    results,
    totals: { ok: okCount, failed: failedCount, total: results.length },
    timing: {
      startedAt,
      endedAt,
      durationMs: Date.now() - startMs,
    },
    batchId: batchId || null,
  };
}

/**
 * Build a subset plan containing only the failed & retryable ops from a
 * previous ApplyReport. Used by ApplyReportModal's "Retry failed" button.
 * Returns the same shape runApplyPlan consumes.
 */
export function buildRetryInputs(report) {
  const orderedOps = [];
  const approvedOps = [];
  const skipIds = [];
  const keepIds = [];
  for (const r of report.results) {
    if (r.ok || !r.retryable) continue;
    if (r.step === 'merge-staging') {
      orderedOps.push({ source_staging_id: r.source.id, target_staging_id: r.target.id });
    } else if (r.step === 'merge-approved') {
      approvedOps.push({ source: r.source.id, target: r.target.id });
    } else if (r.step === 'skip') {
      skipIds.push(r.row.id);
    } else if (r.step === 'keep') {
      keepIds.push(r.row.id);
    }
  }
  return {
    skipIds,
    keepIds,
    analysis: { orderedOps, approvedOps, cycles: [], chains: [], hasErrors: false },
  };
}
